import {type CellAddress, type ItemId, useBoardCell, useBoardLabels} from '@github-ui/board-core'
import {clsx} from 'clsx'
import {memo, type ReactNode} from 'react'

import {BoardCardSpacer} from '../window/BoardWindow'
import {useBoardCardWindow} from '../window/use-board-card-window'
import {BoardCardFrame} from './BoardCardFrame'
import styles from './BoardCellFrame.module.css'
import {BoardItemPaging} from './BoardPaging'

export type BoardCellFrameProps<TItem> = {
  address: CellAddress
  /**
   * The cards loaded in this cell, in order. They come from the caller because reading
   * them is the one thing that needs the item type, and keeping it here is what lets
   * `useBoardCell` stay free of the generic.
   *
   * Must be the same array `useBoardData` holds for this cell, so it lines up with the
   * ids the hook returns.
   */
  items: readonly TItem[]
  renderCard: (item: TItem) => ReactNode
  /**
   * What an empty cell says. Falls back to the `emptyCell` label when it is not
   * supplied, but a renderer that returns `null` draws nothing, so a caller can
   * suppress the empty state for a particular cell.
   */
  renderEmptyCell?: (address: CellAddress) => ReactNode
  className?: string
}

/**
 * One cell: the intersection of a column and a row.
 *
 * A pair the caller never supplied and a pair supplied with no items reach this
 * component the same way, so they render identically. That matters because the caller's
 * `cells` is sparse (it holds only the pairs something is in), and a board that drew
 * absent pairs differently would lose a cell every time a column and a row happened not
 * to overlap.
 */
export function BoardCellFrame<TItem>({
  address,
  items,
  renderCard,
  renderEmptyCell,
  className,
}: BoardCellFrameProps<TItem>) {
  const {
    itemIds,
    cellProps,
    bodyProps,
    bodyRef,
    focusedItemIndex,
    isCardFocusPending,
    isEmpty,
    isLoadingItems,
    hasMoreItems,
    cardSetSize,
    isOver,
  } = useBoardCell(address)
  const labels = useBoardLabels()

  // Only the cards on screen are drawn, plus whichever one the board's focus position
  // names, so a cell holding five hundred costs what fits rather than what it holds.
  const {slots, listRef, measureRef} = useBoardCardWindow({
    bodyRef,
    itemIds,
    focusedIndex: focusedItemIndex,
    isFocusPending: isCardFocusPending,
  })

  // The default copy stands in only when the caller supplied no renderer at all. A
  // renderer that returns `null` has decided this cell shows nothing, and falling back
  // there would overrule it, the same reading of a render prop's `null` that
  // `BoardRowFrame` takes.
  const emptyState =
    renderEmptyCell === undefined ? <p className={styles.Empty}>{labels.emptyCell}</p> : renderEmptyCell(address)

  return (
    <div {...cellProps} data-drop-target={isOver ? '' : undefined} className={clsx(styles.CellFrame, className)}>
      <div {...bodyProps} className={styles.Body}>
        {/*
          The list renders whether or not the cell holds cards. "list, 0 items" tells a
          reader more than silence does, and it gives keyboard reordering a stable
          destination in exactly the cells a person most needs to reach.

          Its window draws a run of cards and reserves the height of the rest, so what the
          scrollbar describes is every card the cell has loaded. Each card states where it
          sits and how many there are, because a browser can only count the ones in the
          page: without it a card would be announced as one of twelve rather than as the
          two hundredth of eight hundred. The list container's own count is still the
          drawn one -- nothing can state a size on a list -- so the honest number is
          carried by the cell's name instead, which a reader hears on the way in, before
          the list states the window's count. Both come from `cardSetSize`, so the number
          in the name and the number on the cards are the same number.
        */}
        <ul ref={listRef} className={styles.Cards}>
          {slots.map(slot => {
            if (slot.type === 'space') {
              return <BoardCardSpacer key={`reserved:${slot.before}`} size={slot.size} />
            }

            const id = itemIds[slot.index]
            const item = items[slot.index]

            return id === undefined || item === undefined ? null : (
              <BoardWindowedCard
                // Both kinds of key are namespaced, because an item id is opaque and may be
                // any string at all, including one that reads exactly like a reservation's.
                key={`card:${id}`}
                itemId={id}
                address={address}
                item={item}
                renderCard={renderCard}
                index={slot.index}
                setSize={cardSetSize}
                measureRef={measureRef}
              />
            )
          })}
        </ul>
        {/*
          "No cards" is a statement about the whole cell, so it is wrong in a cell that
          has a page waiting: such a cell is not empty, it is unread. `hasMoreItems`
          rather than a count, because the count is a fact the caller stated and may well
          be larger than the nothing that is loaded.
        */}
        {isEmpty && !isLoadingItems && !hasMoreItems ? emptyState : null}
        {/*
          Both halves of "there is more in this cell": the ghost that says so, and the
          page-shaped skeletons that replace it while the page is in flight. They render
          after the cards, so a cell loading its first page shows them alone and one
          loading its next shows them below the last card it holds.
        */}
        <BoardItemPaging address={address} />
      </div>
    </div>
  )
}

type BoardWindowedCardProps<TItem> = {
  itemId: ItemId
  address: CellAddress
  item: TItem
  renderCard: (item: TItem) => ReactNode
  index: number
  setSize: number
  measureRef: (node: Element | null) => (() => void) | undefined
}

function BoardWindowedCardImpl<TItem>({
  itemId,
  address,
  item,
  renderCard,
  index,
  setSize,
  measureRef,
}: BoardWindowedCardProps<TItem>) {
  return (
    <BoardCardFrame itemId={itemId} address={address} place={{index, setSize, measureRef}}>
      {renderCard(item)}
    </BoardCardFrame>
  )
}

/**
 * One drawn card, and the boundary that stops an arrow key redrawing the rest of them.
 *
 * A cell re-renders when the board's focus position moves into or out of it, because the
 * card that position names is one the window has to draw wherever it is. Without a boundary
 * here, that would run the caller's `renderCard` again for every card in the window on
 * every keypress -- and a reader holding an arrow key down is the one moment the board can
 * least afford it.
 *
 * The props are the scalars the cell already has rather than the `place` object, because an
 * object built in the loop would be a new one every render and there would be nothing to
 * compare.
 *
 * `memo` erases the type parameter, so the original signature is put back. It is a
 * restatement of the function's own type rather than a widening of it, which is the
 * standard way to memoise a generic component.
 */
const BoardWindowedCard = memo(BoardWindowedCardImpl) as typeof BoardWindowedCardImpl
